<template>
  <v-container fluid>
    <v-row>
      <v-col
        cols="12"
        md="3"
        class="py-0 pl-0 collection-list">
        <v-card
          class="pa-4"
          elevation="3">
          <h2 class="mb-4">
            Collection
          </h2>
          <div>
            <v-select
              v-model="selectedYear"
              label="Year"
              outlined
              dense
              :items="yearOptions"
              item-text="value"
              item-value="value"
              hide-details
              class="mb-2"
            />
            <search-box
              v-model="search"
              class="mb-4" />

            <v-list
              two-line
              style="overflow-y: auto; height: 70vh"
            >
              <!-- LIST -->
              <div
                v-for="(items, month) in groupedCollections"
                :key="month"
              >
                <span class="font-weight-bold">{{ month }}</span>

                <v-list-item
                  v-for="item in items"
                  :key="item.id"
                  @click="onSelect(item)"
                >
                  <v-list-item-content>
                    <v-list-item-title>
                      {{ item.salesDate | dateUserFormatMonth }} - {{ item.name }}
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </div>

              <div
                v-if="loadingCollection"
                class="pa-4 text-center"
              >
                <v-progress-circular
                  indeterminate
                  size="24"
                />
              </div>

              <div
                v-else-if="!loadingCollection && collections.length === 0"
                class="pa-4 text-center"
              >
                No results
              </div>
            </v-list>
          </div>
        </v-card>
      </v-col>

      <v-col
        cols="12"
        md="9"
        class="py-0 pl-0 print-packing-list"
      >
        <img-popup-preview
          v-model="previewImgEnabled"
          :img-src="currentImgPreview" />
        <v-card
          class="d-flex flex-column table-wrapper"
        >
          <v-card-title class="d-flex align-center">
            <div v-if="selectedCollection">
              <p class="headline font-weight-bold mb-0 pl-0">
                {{ selectedCollection.name }}
              </p>
              <p class="text-h6 mb-0 font-weight-medium">
                ทั้งหมด {{ prototypes.length }} แบบ
              </p>
              <p class="text-h6 mb-0 font-weight-medium">
                วันขาย {{ selectedCollection.salesDate | dateUserFormat }}
              </p>
            </div>
            <v-spacer></v-spacer>
            <v-btn
              class="export-btn"
              :disabled="!prototypes.length"
              color="primary"
              @click="exportCsv"
            >
              Export CSV
            </v-btn>
            <v-btn
              :disabled="!prototypes.length"
              color="secondary"
              class="ml-3 print-btn"
              @click="printTable"
            >
              Print
            </v-btn>
          </v-card-title>

          <v-divider></v-divider>

          <v-card-text>
            <div
              v-if="loadingPrototypes"
              class="pa-4 text-center"
            >
              <v-progress-circular
                indeterminate
                size="24" />
            </div>

            <div
              v-else-if="prototypes.length"
              style="overflow-y: auto;"
            >
              <table class="table">
                <thead>
                  <tr>
                    <th
                      v-for="header in headers"
                      :key="header.value"
                      :class="header.classCss"
                      :style="`min-width: ${header.width}px; white-space: nowrap;`"
                    >
                      {{ header.text }}
                    </th>
                  </tr>
                </thead>

                <tbody>
                  <tr
                    v-for="prototype in prototypes"
                    :key="prototype.id"
                    class="text-center"
                  >
                    <td>
                      <div class="image-cell">
                        <img
                          v-if="getPhotoUrl(prototype) !== ''"
                          :src="getPhotoUrl(prototype)"
                          class="main-image"
                          alt="product-image"
                          contain
                          @click="previewImgEnabled = true ,currentImgPreview = getPhotoUrl(prototype)">
                        <img
                          v-else
                          :src="noImgAvailable"
                          class="main-image"
                          alt="product-image">
                      </div>
                    </td>
                    <td>
                      <p class="black--text">
                        {{ get(prototype, 'sketch.sketchId') }}
                      </p>
                    </td>
                    <td>
                      <p class="black--text">
                        {{ get(prototype, 'sketch.factory.name') }}
                      </p>
                    </td>
                    <td>
                      <span
                        v-for="(po, index) in prototype.purchaseOrders"
                        :key="po.id">
                        <span
                          class="po-link"
                          @click="showPo(po.id)"
                        >{{ po.orderNo }}</span><span v-if="index !== prototype.purchaseOrders.length - 1">, </span>
                      </span>
                    </td>
                    <td>
                      <p class="black--text">
                      </p>
                    </td>
                    <td>
                      <p class="black--text">
                      </p>
                    </td>
                    <td>
                      <p class="black--text">
                      </p>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div
              v-else
              class="pa-4 text-center">
              ไม่พบ Prototype ใน Collection นี้
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapActions } from 'vuex/dist/vuex.common'
import ProductCollectionProvider from '@/resources/ProductCollectionProvider'
import SearchBox from '@/components/SearchBox.vue'
import NoImgAvailable from '@/assets/image/no_picture_available.png'
import ProductionPlanProvider from '@/resources/ProductionPlanProvider'
import ImgPopupPreview from '@/components/ImgModalPreview.vue'
import { ExportToCsv } from 'export-to-csv'
import getPhotoUrl from '@/assets/js/PhotoUrl'

const ProductCollectionService = new ProductCollectionProvider()
const ProductionPlanService = new ProductionPlanProvider()

export default {
  components: {
    SearchBox,
    ImgPopupPreview
  },
  data () {
    return {
      initLoading: false,
      loadingCollection: false,
      loadingPrototypes: false,
      selectedCollection: null,
      totalItems: 0,
      totalPages: 0,
      collections: [],
      prototypes: [],
      options: {
        page: 1,
        itemsPerPage: 10,
        sortBy: ['createdAt'],
        sortDesc: [true]
      },
      search: '',
      isFetchingByScroll: false,
      headers: [
        {
          text: 'Image',
          value: 'sketch.images[0]',
          align: 'center',
          width: 150,
          classCss: 'sticky-col col-1'
        },
        {
          text: 'Sketch ID',
          value: 'sketch.sketchId',
          align: 'center',
          width: 100,
          classCss: 'sticky-col col-2'
        },
        {
          text: 'Factory',
          value: 'sketch.factory.name',
          align: 'center',
          width: 100
        },
        {
          text: 'PO',
          value: 'sketch.po',
          align: 'center',
          width: 100
        },
        {
          text: 'Stock QTY',
          value: 'sketch.stockQty',
          align: 'center',
          width: 50
        },
        {
          text: '',
          value: '1',
          align: 'center',
          width: 50
        },
        {
          text: '',
          value: '2',
          align: 'center',
          width: 50
        }
      ],
      selectedYear: this.$dayjs().year(),
      yearOptions: [
        { value: 2024 },
        { value: 2025 },
        { value: 2026 },
        { value: 2027 },
        { value: 2028 }
      ],
      noImgAvailable: NoImgAvailable,
      previewImgEnabled: false,
      currentImgPreview: '',
      getPhotoUrl
    }
  },
  computed: {
    groupedCollections () {
      const sorted = [...this.collections].sort(
        (a, b) => new Date(b.salesDate) - new Date(a.salesDate)
      )

      return sorted.reduce((acc, item) => {
        const key = this.$dayjs(item.salesDate).format('MMM YYYY').toUpperCase()
        if (!acc[key]) acc[key] = []
        acc[key].push(item)
        return acc
      }, {})
    }
  },
  watch: {
    selectedYear () {
      this.getCollection(false, true)
    },
    search () {
      clearTimeout(this.delaySearch)
        this.delaySearch = setTimeout(() => {
        this.getCollection(false, true)
      }, 300)
    }
  },
  async mounted () {
    this.initLoading = true
    await this.getCollection()
    this.initLoading = false
  },
  methods: {
    ...mapActions({
      setSnackbar: 'Components/setSnackbar',
      setErrorPage: 'Components/setErrorPage',
      setModal: 'Components/setModal'
    }),
    get (obj, path, fallback = '-') {
      try {
        return path.split('.').reduce((o, i) => o[i], obj) ?? fallback
      } catch (_) {
        return fallback
      }
    },
    async getCollection (append = false, reset = false) {
      if (this.loadingCollection) return
      try {
        if (reset) {
          this.options.page = 1
          this.totalPages = 0
        }

        this.loadingCollection = true
        const sortBy = this.options.sortBy[0]
        const sortOrder = this.options.sortDesc[0] ? 'desc' : 'asc'

        const { data } = await ProductCollectionService.getAll({
          ...this.options,
          sortBy,
          sortOrder,
          search: this.search,
          year: this.selectedYear
        })

        this.totalItems = data.total
        this.totalPages = data.pages

        data.results = data.results.filter((item) => item.salesDate)

        if (append) {
          this.collections.push(...data.results)
        } else {
          this.collections = data.results
        }
      } catch (error) {
        this.setSnackbar({
          value: true,
          message: error.message,
          type: 'error'
        })
      } finally {
        this.loadingCollection = false
      }
    },
    onSelect (item) {
      this.selectedCollection = item
      this.prototypes = []
      this.getPrototypesByCollectionId(item.id, item.name)
    },
    async getPrototypesByCollectionId (collectionId, collectionName) {
      try {
        this.loadingPrototypes = true
        const { data } = await ProductionPlanService.getCollectionPlaning({
          collectionId,
          collectionName,
          year: this.selectedYear
        })
        this.prototypes = data
      } catch (error) {
        this.setSnackbar({
          value: true,
          message: error.message,
          type: 'error'
        })
      } finally {
        this.loadingPrototypes = false
      }
    },
    exportCsv () {
      if (!this.prototypes.length) return

      const timeStringFormat = this.$dayjs().format('DD_MM_YYYY_HHmm')

      const options = {
        filename: `packing_list_${timeStringFormat}`,
        showLabels: true,
        useKeysAsHeaders: true,
        useBom: true
      }

      const exportData = this.prototypes.map((item) => ({
        'Sketch ID': this.get(item, 'sketch.sketchId'),
        'Factory': this.get(item, 'sketch.factory.name'),
        'PO': (item.purchaseOrders || [])
          .map((po) => po.orderNo)
          .join(', '),
        'Stock QTY': this.get(item, 'sketch.stockQty', '')
      }))

      const csvExporter = new ExportToCsv(options)
      csvExporter.generateCsv(exportData)
    },
    printTable () {
      window.print()
    },
    showPo (poId) {
      if (!poId) return

      const routeData = this.$router.resolve({
        name: 'DeliveryNoteOfPurchaseOrder',
        params: { id: poId }
      })
      window.open(routeData.href, '_blank')
    }
  }
}
</script>

<style scoped>
.table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
}
.table th {
  border: 1px solid #e0e0e0;
  padding: 6px 8px;
  vertical-align: top;
}
.table td {
  border: 1px solid #e0e0e0;
  padding: 6px 8px;
  vertical-align: top;
  height: 100%;
  width: 200px;
}
.main-image {
  width: 80px;
}

.table-wrapper {
  max-height: calc(100vh - 78px);
  overflow-y: scroll;
}

.image-cell {
  display: flex;
  justify-content: center;
}
.po-link {
  cursor: pointer;
  color: #1976d2;
  text-decoration: underline;
}
.po-link:hover {
  opacity: 0.8;
}
</style>

<style>
@media print {
  .table {
    width: 100% !important;
    table-layout: fixed;
    font-size: 10px;
  }

  .table th,
  .table td {
    white-space: normal !important;
    word-wrap: break-word;
    width: 90px
  }

  thead {
    display: table-header-group;
  }

  tr {
    page-break-inside: avoid;
    break-inside: avoid;
  }

  .btnPo {
    height: 0px !important;
    padding: 0 !important;
  }

  a {
    text-decoration: none !important;
    color: black !important;
  }

  .sticky-col {
    position: static !important;
  }

  .table-wrapper {
    max-height: none !important;
    overflow: visible !important;
  }

  .main-image {
    width: 70px;
  }

  .export-btn {
    display: none !important;
  }

  .print-btn {
    display: none !important;
  }

  .v-divider {
    display: none !important;
  }

  .po-link {
    color: black !important;
    text-decoration: none !important;
  }

  .print-packing-list {
    page-break-before: auto;
    page-break-after: auto;
  }

  .collection-list {
    display: none;
  }

  .v-sheet.theme--light.v-toolbar.v-toolbar--dense.v-app-bar.v-app-bar--fixed {
    display: none !important;
  }

  .v-sheet.v-card:not(.v-sheet--outlined) {
    -webkit-box-shadow: none;
    box-shadow: none;
  }

  #sidebar {
    display: none !important;
  }

  #app main.v-main {
    padding: 0 !important;
  }
}
</style>
